/**
 * HpQuickInput — damage / healing in 2–3 actions, by the rules (owner's charsheet pass, 24.10: «7 урона = 7 кликов»,
 * «−5 в поле хитов ставит 0»).
 *
 * One field, three ways to write:
 *   «−7» / «-7» + Enter, or «7» + «Урон»   → damage 7 (temporary hit points first, 0 → dying, at 0 → a death save
 *                                             failure, «крит» → two) — sheet:apply-damage
 *   «+5» + Enter, or «5» + «Лечение»        → healing 5 (above 0 ends dying) — sheet:apply-healing
 *   «=12» + Enter                            → set the hit points to 12 (a correction; still by the death rules)
 * A bare number + Enter is not guessed: the hint asks for a sign or a button.
 */
import { useState } from 'react';
import type { HudCharacterData, CharacterHudStore } from '../../lib/character-hud-store';
import { applyDamageToHp, applyHealingToHp } from '@roll1/shared/systems/dnd5e/damage.js';
import { useI18n } from '../../i18n';
import { hudDeathStatus } from './death-state';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import ConfirmDialog from '../ConfirmDialog';

export type HpIntent = { kind: 'damage' | 'heal' | 'set'; amount: number } | null;

/** «−7» → damage 7, «+5» → heal 5, «=12» → set 12, «7» with a button → that button. */
export function parseHpInput(raw: string, button?: 'damage' | 'heal'): HpIntent {
  const s = raw.trim().replace(/−/g, '-').replace(/\s+/g, '');
  const m = /^([-+=]?)(\d{1,4})$/.exec(s);
  if (!m) return null;
  const n = Number(m[2]);
  if (m[1] === '-') return n > 0 ? { kind: 'damage', amount: n } : null;
  if (m[1] === '+') return n > 0 ? { kind: 'heal', amount: n } : null;
  if (m[1] === '=') return { kind: 'set', amount: n };
  if (button && n > 0) return { kind: button, amount: n };
  return null;
}

interface HpQuickInputProps {
  data: HudCharacterData;
  store: CharacterHudStore;
  send?: (msg: unknown) => void;
  sheetId?: string;
  /** Ask before damage (the campaign's «confirm damage» setting). */
  confirmDamage?: boolean;
  compact?: boolean;
}

export function HpQuickInput({ data, store, send, sheetId, confirmDamage = false, compact = false }: HpQuickInputProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const [crit, setCrit] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const status = hudDeathStatus(data);
  const dead = status === 'dead';
  const atZero = data.hp.current <= 0;
  const disabled = !send || dead;

  // The «confirm damage» question waits in the project's own dialog, not the browser's (modals brief 28.10).
  const [askDamage, setAskDamage] = useState<number | null>(null);

  const apply = (intent: HpIntent, confirmed = false) => {
    if (!intent) { setHint(t('characterHud.hpInputHint')); return; }
    setHint(null);
    if (intent.kind === 'damage') {
      if (confirmDamage && !confirmed) { setAskDamage(intent.amount); return; }
      if (send && sheetId) {
        send({ type: 'sheet:apply-damage', sheetId, amount: intent.amount, ...(crit && atZero ? { critical: true } : {}) });
      } else {
        const { hp, temp } = applyDamageToHp(data.hp.current, data.hp.temp, intent.amount);
        store.patch({ hp: { current: hp, temp } });
      }
    } else if (intent.kind === 'heal') {
      if (send && sheetId) send({ type: 'sheet:apply-healing', sheetId, amount: intent.amount });
      else store.patch({ hp: { current: applyHealingToHp(data.hp.current, data.hp.max, intent.amount) } });
    } else {
      const next = Math.max(0, Math.min(data.hp.max, intent.amount));
      if (send && sheetId) send({ type: 'sheet:update', sheetId, changes: { hp: next } });
      else store.patch({ hp: { current: next } });
    }
    setDraft('');
    setCrit(false);
  };

  // GN: the UI vocabulary (Input / Button / Checkbox, text scale) in the paper theme; the field is wide enough for its
  // hint «−7 / +5» (re-pass Н23: cut to «-7 / +!» at 1280).
  const h = compact ? 'h-8' : 'h-9';
  return (
    <div data-testid="hp-quick-input" className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-stretch gap-1">
        <Input
          type="text"
          inputMode="text"
          aria-label={t('characterHud.hpInputLabel')}
          placeholder={t('characterHud.hpInputPlaceholder')}
          value={draft}
          disabled={disabled}
          onChange={(e) => { setDraft(e.target.value); setHint(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(parseHpInput(draft)); } }}
          className={`${h} ${compact ? 'w-24' : 'w-28'} font-mono text-body`}
        />
        <Button type="button" variant="outline" data-testid="hp-damage" disabled={disabled} className={`${h} font-semibold text-hl`}
          onClick={() => apply(parseHpInput(draft, 'damage'))}>
          {t('characterHud.hpDamage')}
        </Button>
        <Button type="button" variant="outline" data-testid="hp-heal" disabled={disabled} className={`${h} font-semibold text-ok`}
          onClick={() => apply(parseHpInput(draft, 'heal'))}>
          {t('characterHud.hpHeal')}
        </Button>
      </div>
      {atZero && !dead && (
        <label className="flex min-h-6 items-center gap-1.5 text-meta text-dim">
          <Checkbox checked={crit} onCheckedChange={(v) => setCrit(v === true)} />
          {t('characterHud.hpCritAtZero')}
        </label>
      )}
      {(hint || dead) && (
        <div role="status" className={`text-meta ${dead ? 'text-hl' : 'text-dim'}`}>
          {dead ? t('characterHud.hpDeadHint') : hint}
        </div>
      )}
      <ConfirmDialog
        open={askDamage !== null}
        onOpenChange={(open) => { if (!open) setAskDamage(null); }}
        title={t('combat.confirmDamagePrompt').replace('{damage}', String(askDamage ?? 0)).replace('{name}', data.identity.name)}
        description=""
        tone="danger"
        onConfirm={() => { if (askDamage !== null) apply({ kind: 'damage', amount: askDamage }, true); }}
      />
    </div>
  );
}
